/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Address } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { Heading as HeadingData } from '@msdyn365-commerce-modules/data-types';
import { Heading, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { get } from 'mobx';
import * as React from 'react';

import {
    AddressItemDisplayType,
    AddressItemType,
    AddressValidationRuleType,
    IAddressDropdownsData,
    IAddressItem,
    IAddressValidationRule
} from '../address-format.data';
import { AddressType, IAddressResource, IAddressResponse } from '../../common/address-module.data';
import AddressAlertComponent from './address-alert';
import AddressButtonComponent from './address-button';
import AdressCheckboxComponent from './address-checkbox';
import AdressDropdownComponent from './address-dropdown';
import AddressErrorMessageComponent from './address-error-message';
import AddressErrorTitleComponent from './address-error-title';
import AddressInputComponent from './address-input';
import AddressLabelComponent from './address-label';
import { IPostCodeDropDownOption } from '../..';
import { IActionContext, ICoreContext } from '@msdyn365-commerce/core';
export interface IAddressAddInputProps {
    isUpdating?: boolean;
    hasError?: boolean;
    hasExternalSubmitGroup?: boolean;
    addressType: AddressType;
    addressFormat: IAddressItem[];
    defaultCountryRegionId: string;
    defaultAddressType: number;
    selectedAddress?: Address;
    dropdownDisplayData: IAddressDropdownsData;
    resources: IAddressResource;
    addressActionResponse?: IAddressResponse;
    addAddressHeading?: HeadingData;
    editAddressHeading?: HeadingData;
    validationError?: object;
    telemetryContent?: ITelemetryContent;
    postalOptions: IPostCodeDropDownOption[];
    shouldAutoFocus?: boolean;
    onInputChange(event: React.ChangeEvent<HTMLInputElement>): void;
    onDropdownChange(event: React.ChangeEvent<HTMLSelectElement>): void;
    onSave?(): void;
    onCancel?(): void;
    ctx?: IActionContext;
    productIds?: number[];
    hasStartedTypingEmail?: boolean;
    setHasStartedTypingEmail?: React.Dispatch<React.SetStateAction<boolean>>;
    addressSuggestions?: JSX.Element;
    productsRestrictedPostcodes?: string[];
}

export interface IAddressError {
    AddressError: INodeProps;
    title: React.ReactNode;
    message: React.ReactNode;
}

export interface IAddressAddItem {
    key: string;
    AddressItem: INodeProps;
    label: React.ReactNode;
    alert: React.ReactNode;
    input: React.ReactNode;
}

export interface IAddressAddUpdateProps {
    AddressForm: INodeProps;
    heading: React.ReactNode;
    items: IAddressAddItem[];
    hasError: boolean;
    error: IAddressError;
    isShowSaveButton: boolean;
    saveButton: React.ReactNode;
    isShowCancelButton: boolean;
    cancelButton: React.ReactNode;
    hasStartedTypingEmail?: boolean;
    setHasStartedTypingEmail?: React.Dispatch<React.SetStateAction<boolean>>;
    context?: ICoreContext | undefined;
}

const getRequriedAttribute = (validationRules?: IAddressValidationRule[]): object => {
    const requriedRule = (validationRules || []).find(validationRule => {
        return validationRule.type === AddressValidationRuleType.Required;
    });

    return requriedRule ? { 'aria-required': true } : {};
};

const getAddessItems = (selectedAddress: Address, props: IAddressAddInputProps): IAddressAddItem[] => {
    const {
        addressFormat,
        addressType,
        dropdownDisplayData,
        defaultCountryRegionId,
        defaultAddressType,
        validationError = {},
        onInputChange,
        onDropdownChange,
        shouldAutoFocus,
        ctx,
        productIds
    } = props;
    return addressFormat.map((addressFormatItem, index) => {
        const elementId = `${addressType.toLowerCase()}_address${addressFormatItem.name.toLowerCase()}`;
        const errorMessage = get(validationError, addressFormatItem.name);
        const className = classname('msc-address-form__item', `msc-address-form__item-${addressFormatItem.name.toLowerCase()}`, {
            'msc-address-form__item-newline': addressFormatItem.isNewLine,
            'address-form__item-invalid': errorMessage
        });
        let input;

        if (addressFormatItem.displayType === AddressItemDisplayType.Input) {
            input = (
                <AddressInputComponent
                    {...{
                        id: elementId,
                        name: addressFormatItem.name,
                        className: 'msc-address-form',
                        type: 'text',
                        autoFocus: shouldAutoFocus ?? index === 0,
                        value: (selectedAddress as any)[addressFormatItem.name],
                        maxLength: addressFormatItem.maxLength,
                        onChange: onInputChange,
                        additionalAddributes: getRequriedAttribute(addressFormatItem.validationRules),
                        ctx: ctx,
                        productIds: productIds
                    }}
                />
            );
        } else if (addressFormatItem.displayType === AddressItemDisplayType.Checkbox) {
            input = (
                <AdressCheckboxComponent
                    {...{
                        id: elementId,
                        name: addressFormatItem.name,
                        className: 'msc-address-form',
                        type: 'checkbox',
                        autoFocus: index === 0,
                        isChecked: (selectedAddress as any)[addressFormatItem.name],
                        onChange: onInputChange,
                        additionalAddributes: getRequriedAttribute(addressFormatItem.validationRules)
                    }}
                />
            );
        } else {
            const displayData = dropdownDisplayData[addressFormatItem.name];
            let selectedValue = (selectedAddress as any)[addressFormatItem.name];

            if (addressFormatItem.type === AddressItemType.ThreeLetterISORegionName) {
                selectedValue = selectedValue || defaultCountryRegionId;
            }

            if (addressFormatItem.type === AddressItemType.AddressTypeValue) {
                selectedValue = selectedValue || defaultAddressType;
            }

            input = (
                <AdressDropdownComponent
                    {...{
                        id: elementId,
                        name: addressFormatItem.name,
                        className: 'msc-address-form',
                        value: selectedValue,
                        displayData,
                        onChange: onDropdownChange,
                        additionalAddributes: getRequriedAttribute(addressFormatItem.validationRules)
                    }}
                />
            );
        }

        return {
            key: addressFormatItem.name,
            AddressItem: { className, id: `${elementId}_container` },
            label: (
                <AddressLabelComponent
                    {...{
                        id: elementId,
                        text:
                            addressFormatItem.label.toLowerCase() === 'name *'
                                ? 'Full name (first name and surname)*:'
                                : addressFormatItem.label
                    }}
                />
            ),
            alert: <AddressAlertComponent {...{ message: errorMessage }} />,
            input
        };
    });
};
const getInputLabel = (inputName: string) => {
    // if (inputName.toLowerCase() === 'Surname*:') {
    //     return 'Surname*:';
    // }
    if (inputName.toLowerCase() === 'name*:') {
        return 'Full name (first name and surname)*:';
    }
    if (inputName.toLowerCase().includes('phone')) {
        return 'Phone*';
    }
    if (inputName.toLowerCase().includes('zip code')) {
        return 'Postcode*';
    }
    if (inputName.toLowerCase().includes('city')) {
        return 'Town/city*';
    }
    if (inputName.toLowerCase().includes('street')) {
        return 'Address*';
    }

    if (inputName.toLowerCase().includes('country')) {
        return '';
    }

    return inputName;
};
const postcodePattern = /^([A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s?\d[A-Za-z]{2})$/;
const areRequiredFieldsValid = (
    selectedAddress: Address,
    addressFormat: IAddressItem[],
    productsRestrictedPostcodes?: string[]
): boolean => {
    const isValidEmail = (value?: string) => {
        if (!value) {
            return false;
        }
        if (!value.includes('@')) {
            return false;
        }
        if ((value.match(/@/g) || []).length > 1) {
            return false;
        }
        if (!/\.[^@]+$/.test(value.split('@')[1] || '')) {
            return false;
        }
        if (!/@[^@.]+\./.test(value)) {
            return false;
        }
        if (/\s/.test(value)) {
            return false;
        }
        return true;
    };
    for (const item of addressFormat) {
        const fieldName = item.name as keyof Address;
        const value = selectedAddress[fieldName];

        if (item.displayType === AddressItemDisplayType.Checkbox && (value === undefined || value === null)) {
            return false;
        }

        if (item.displayType !== AddressItemDisplayType.Checkbox && (!value || String(value).trim() === '')) {
            return false;
        }

        if (item.name.toLowerCase().includes('phone')) {
            const digitsOnly = (value || '').toString().replace(/\D/g, '');
            if (digitsOnly.length < 9 || digitsOnly.length > 11) {
                return false;
            }
        }

        if (item.name.toLowerCase().includes('zipcode')) {
            const val = (value || '').toString().trim();
            const isRestrictedCode = productsRestrictedPostcodes?.some(postCode => val.toLowerCase().startsWith(postCode.toLowerCase()));
            if (!val || val.length < 5 || val.length > 8 || !postcodePattern.test(val) || isRestrictedCode) {
                return false;
            }
        }
    }

    if (!isValidEmail(selectedAddress['Email'])) {
        return false;
    }

    return true;
};

export const AddressAddUpdate = (props: IAddressAddInputProps): IAddressAddUpdateProps => {
    const {
        editAddressHeading,
        addAddressHeading,
        selectedAddress = {},
        resources,
        hasError,
        onSave,
        hasExternalSubmitGroup,
        isUpdating,
        addressActionResponse,
        telemetryContent,
        hasStartedTypingEmail,
        productsRestrictedPostcodes
    } = props;
    const heading = selectedAddress.RecordId ? editAddressHeading : addAddressHeading;

    const getOrderChangedAddressItems = (addrItems: IAddressAddItem[], selectedAddress: Address, props: IAddressAddInputProps) => {
        const orderChangedAddressItems: IAddressAddItem[] = [];
        const fullName = addrItems.find(item => item.key.toLowerCase().includes('name'));
        const phone = addrItems.find(item => item.key.toLowerCase().includes('phone'));
        const street = addrItems.find(item => item.key.toLowerCase().includes('street'));
        const zipcode = addrItems.find(item => item.key.toLowerCase().includes('zipcode'));
        const city = addrItems.find(item => item.key.toLowerCase().includes('city'));
        const emailElemId = `${'Email'.toLowerCase()}_address${'email'}`;

        const isValidEmail = (value?: string) => {
            if (!value) {
                return 'Email is missing.';
            }

            const parts = value.split('@');

            if (parts.length !== 2) {
                if (parts.length === 1) {
                    return 'Email is missing the @ character.';
                }
                if (parts.length > 2) {
                    return 'Email has more than one @ character.';
                }
            }

            if (!parts[0]) {
                return 'Email must have characters before the @.';
            }

            if (!/\.[^@]+$/.test(parts[1] || '')) {
                return 'Email is missing the domain i.e. .co.uk.';
            }
            if (!/@[^@.]+\./.test(value)) {
                return 'Email must have a domain name between @ and .';
            }
            if (/\s/.test(value)) {
                return 'Email must not contain spaces.';
            }
            return null;
        };
        const emailErrorMessage = isValidEmail(selectedAddress['Email']);

        const emailInput = (
            <AddressInputComponent
                {...{
                    id: emailElemId,
                    name: 'Email',
                    className: 'msc-address-form',
                    type: 'text',
                    autoFocus: false,
                    value: selectedAddress['Email'] || '',
                    maxLength: 100,
                    onChange: props.onInputChange,
                    additionalAddributes: {}
                }}
            />
        );

        const emailClassName = classname('msc-address-form__item', `msc-address-form__item-${'email'}`, {
            'msc-address-form__item-newline': false,
            'address-form__item-invalid': 'Please enter a valid email address'
        });

        const emailAddressItem = {
            key: 'Email',
            AddressItem: { className: emailClassName, id: `${emailElemId}_container` },
            label: <AddressLabelComponent {...{ id: emailElemId, text: getInputLabel('Email*:') }} />,
            alert: hasStartedTypingEmail && emailErrorMessage && <AddressAlertComponent {...{ message: emailErrorMessage }} />,
            input: emailInput
        };

        if (fullName) {
            orderChangedAddressItems.push(fullName);
        }

        orderChangedAddressItems.push(emailAddressItem);

        if (phone) {
            orderChangedAddressItems.push(phone);
        }

        if (street) {
            orderChangedAddressItems.push(street);
        }

        if (props.addressSuggestions) {
            const addressSuggestionsItem = {
                key: 'AddressSuggestions',
                AddressItem: { className: 'check1', id: `addressSuggestions_container` },
                label: <></>,
                alert: <></>, //<AddressAlertComponent {...{ message: errorMessage }} />,
                input: props.addressSuggestions
            };
            orderChangedAddressItems.push(addressSuggestionsItem);
        }

        if (city) {
            orderChangedAddressItems.push(city);
        }
        if (zipcode) {
            orderChangedAddressItems.push(zipcode);
        }
        return orderChangedAddressItems;
    };

    const allValid = areRequiredFieldsValid(selectedAddress, props.addressFormat, productsRestrictedPostcodes);

    return {
        AddressForm: { className: 'msc-address-form' },
        heading: heading && <Heading className='msc-address-form__heading' {...heading} />,
        items: getOrderChangedAddressItems(getAddessItems(selectedAddress, props), selectedAddress, props),
        isShowSaveButton: !hasExternalSubmitGroup,
        saveButton: onSave && (
            <AddressButtonComponent
                {...{
                    className: classname('msc-address-form__button-save msc-btn', {
                        'msc-address-form__button-updating': isUpdating || !allValid
                    }),
                    // text: resources.addressSaveButtonText,
                    text: 'Save delivery address',
                    ariaLabel: resources.addressSaveButtonAriaLabel,
                    disabled: isUpdating || !allValid,
                    onClick: onSave,
                    telemetryContent
                }}
            />
        ),
        isShowCancelButton: !hasExternalSubmitGroup,
        cancelButton: <></>,
        hasError: hasError || false,
        error: {
            AddressError: { className: 'msc-address-form__error' },
            title: addressActionResponse && addressActionResponse.errorTitle && (
                <AddressErrorTitleComponent {...{ title: addressActionResponse.errorTitle || '' }} />
            ),
            message: addressActionResponse && addressActionResponse.errorMessage && (
                <AddressErrorMessageComponent {...{ message: addressActionResponse.errorMessage || '' }} />
            )
        }
    };
};
